import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle, AlertTriangle } from "lucide-react";
import { api, inr, fmtDateTime } from "@/lib/api";
import { PageTitle, Stat, Panel } from "@/components/admin/AdminLayout";
import { Progress } from "@/components/ui/progress";

const LINKS = { password: "/admin/settings", site: "/admin/settings", network: "/admin/catalog", merchant: "/admin/catalog", deals: "/admin/import", ai: "/admin/settings", workflows: "/admin/automation", demo: "/admin/settings" };

const Wizard = () => {
  const { data } = useQuery({ queryKey: ["setup"], queryFn: () => api.get("/admin/setup-status").then((r) => r.data) });
  if (!data) return null;
  const done = data.steps.filter((s) => s.done).length;
  return (
    <Panel title={`Setup wizard · ${done}/${data.steps.length} done`} testid="setup-wizard">
      <Progress value={(done / data.steps.length) * 100} className="h-2 mb-4" />
      <ol className="grid sm:grid-cols-2 gap-2">
        {data.steps.map((s, i) => (
          <li key={s.key}>
            <Link to={LINKS[s.key]} data-testid={`setup-step-${s.key}`} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 text-sm">
              {s.done ? <CheckCircle2 className="text-emerald-600 shrink-0" size={18} /> : <Circle className="text-slate-300 shrink-0" size={18} />}
              <span className={s.done ? "text-slate-400 line-through" : "font-semibold"}>{i + 1}. {s.label}</span>
            </Link>
          </li>
        ))}
      </ol>
    </Panel>
  );
};

export default function Dashboard() {
  const qc = useQueryClient();
  const { data: o } = useQuery({ queryKey: ["overview"], queryFn: () => api.get("/admin/overview").then((r) => r.data) });
  if (!o) return <div className="h-96 bg-slate-200/60 rounded-xl animate-pulse" />;
  const c = o.commissions;
  const pct = Math.min(100, (o.ai.month_spent_inr / Math.max(1, o.ai.month_budget_inr)) * 100);
  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <PageTitle title="Overview" sub="Everything at a glance. Clicks are not sales, and pending commissions are not confirmed earnings." />
      {o.demo_records > 0 && (
        <div className="bg-purple-50 border border-purple-200 text-purple-800 rounded-xl p-4 text-sm" data-testid="demo-data-warning">
          {o.demo_records} demo deals are loaded so you can explore. Remove them from <Link to="/admin/settings" className="underline font-bold">Settings</Link> before going live.
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Live deals" value={o.deals.published || 0} hint={`${o.deals_total} total`} tone="green" testid="stat-live-deals" />
        <Stat label="Awaiting review" value={o.deals.pending_review || 0} hint={`${o.pending_drafts} AI drafts`} tone="amber" testid="stat-pending" />
        <Stat label="Clicks this month" value={o.clicks_month} hint={`${o.clicks_total} all time`} testid="stat-clicks" />
        <Stat label="Merchants / coupons" value={`${o.merchants} / ${o.coupons}`} hint={`${o.categories} categories`} testid="stat-merchants" />
        <Stat label="Estimated (pending, all time)" value={inr(c.pending?.inr || 0)} hint="Not confirmed yet" tone="amber" testid="stat-estimated" />
        <Stat label="Confirmed (all time)" value={inr((c.approved?.inr || 0) + (c.paid?.inr || 0))} hint={`Paid: ${inr(c.paid?.inr || 0)}`} tone="green" testid="stat-confirmed" />
        <Stat label="Reversed / rejected (all time)" value={inr((c.reversed?.inr || 0) + (c.rejected?.inr || 0))} tone="red" testid="stat-reversed" />
        <Stat label="Failed import rows" value={o.failed_imports} hint={<Link className="underline" to="/admin/import">Review</Link>} tone={o.failed_imports ? "red" : "slate"} testid="stat-failed-imports" />
      </div>
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <Wizard />
        <div className="space-y-6">
          <Panel title="AI budget this month" testid="ai-budget-meter">
            <p className="font-display font-extrabold text-2xl">{inr(o.ai.month_spent_inr)} <span className="text-sm text-slate-400">of {inr(o.ai.month_budget_inr)}</span></p>
            <Progress value={pct} className="h-2 my-3" />
            <p className="text-xs text-slate-500">{o.ai.today_generations}/{o.ai.daily_limit} generations in the last 24h. {o.ai.budget_exhausted && <b className="text-red-600">Budget used up - AI paused.</b>}</p>
          </Panel>
          <Panel title="Alerts" testid="alerts-panel" action={o.alerts.length > 0 && <button className="text-xs underline" data-testid="alerts-mark-read" onClick={async () => { await api.post("/admin/alerts/read"); qc.invalidateQueries({ queryKey: ["overview"] }); }}>Mark all read</button>}>
            {o.alerts.length === 0 ? <p className="text-sm text-slate-500">No new alerts.</p> : o.alerts.map((a) => (
              <div key={a.id} className="flex gap-2 text-sm py-2 border-b border-slate-100 last:border-0">
                <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
                <div><p className="font-semibold">{a.title}</p><p className="text-xs text-slate-500">{a.message} · {fmtDateTime(a.created_at)}</p></div>
              </div>
            ))}
          </Panel>
        </div>
      </div>
    </div>
  );
}
